import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { AutoRepliesCache } from '@/lib/auto-replies-cache'

// Interface for the metadata structure from obsync
interface ActivityMetadata {
  to_email?: string
  from_email?: string
  campaign_name?: string
  campaign_id?: number
  subject?: string
  reply_body?: string
  event_type?: string
  obsync_id?: string
  [key: string]: any
}

export interface AutoReply {
  id: string
  app_url?: string
  subject?: string
  to_name?: string
  stats_id?: string
  to_email?: string
  cc_emails?: string[]
  client_id?: number
  event_type?: string
  from_email?: string
  from_name?: string
  message_id?: string
  reply_body?: string
  secret_key?: string
  webhook_id?: number
  campaign_id?: number
  description?: string
  webhook_url?: string
  preview_text?: string
  time_replied?: string
  webhook_name?: string
  campaign_name?: string
  sl_lead_email?: string
  campaign_status?: string
  event_timestamp?: string
  sequence_number?: number
  sl_email_lead_id?: number
  sent_message?: {
    text?: string
    html?: string
    time?: string
    message_id?: string
  } | null
  sent_message_body?: string
  sl_email_lead_map_id?: number
  ui_master_inbox_link?: string
  reply_count?: number
  sentiment?: string
  category?: string
  thread_id?: string
  activity_date?: string
  content?: string
  ai_confidence?: number
  
  // Metadata nested fields
  metadata_webhook_created_at?: string
  
  // Sent message nested fields
  sent_message_html?: string
  sent_message_text?: string
  sent_message_time?: string
  sent_message_message_id?: string
  
  // Reply message nested fields
  reply_message_html?: string
  reply_message_text?: string
  reply_message_time?: string
  reply_message_message_id?: string
  
  // Lead correspondence nested fields
  lead_correspondence_target_lead_email?: string
  lead_correspondence_reply_received_from?: string
  lead_correspondence_replied_company_domain?: string
  
  // AI Categorization fields
  response_category?: string
  response_category_id?: string
  reasoning?: string
  response_text?: string
  ai_model_used?: string
  categorized_at?: string
  
  // New columns
  region?: string
  reply_category_type?: string
  
  // Smartlead sync tracking
  smartlead_sync_status?: string
  smartlead_sync_attempted_at?: string
  smartlead_sync_error?: string
  
  // Smartlead ignore reply tracking
  smartlead_ignore_status?: string
  smartlead_ignore_attempted_at?: string
  smartlead_ignore_error?: string
  smartlead_ignore_retry_count?: number
  
  // SDR Response Tracking fields
  average_time_to_respond?: number | null
  lead_without_a_reply?: boolean | null
  sdr_response_tracking_status?: string | null
  first_sdr_response_timestamp?: string | null
  first_positive_reply_timestamp?: string | null
  last_sdr_poll_at?: string | null
  
  // Slack notification tracking
  slack_notification_sent?: boolean | null
  
  // Company domain for enrichment
  company_domain?: string
  
  // Full payload storage
  data: any
  
  // Timestamps
  created_at: string
  updated_at: string
}

export interface AutoRepliesData {
  count: number
  replies: AutoReply[]
}

// Column list shared between main and retry queries
const REPLY_COLUMNS = `
  id,
  subject,
  created_at,
  updated_at,
  client_id,
  event_type,
  to_email,
  to_name,
  from_email,
  campaign_name,
  campaign_id,
  sl_email_lead_id,
  sl_lead_email,
  response_category,
  response_category_id,
  reply_category_type,
  ai_model_used,
  categorized_at,
  reply_count,
  smartlead_sync_status,
  smartlead_sync_attempted_at,
  smartlead_sync_error,
  smartlead_ignore_status,
  smartlead_ignore_attempted_at,
  smartlead_ignore_error,
  smartlead_ignore_retry_count,
  region,
  app_url,
  stats_id,
  message_id,
  event_timestamp,
  webhook_id,
  description,
  preview_text,
  confidence_score,
  average_time_to_respond,
  lead_without_a_reply,
  sdr_response_tracking_status,
  first_sdr_response_timestamp,
  first_positive_reply_timestamp,
  last_sdr_poll_at,
  secret_key,
  slack_notification_sent
`

// Supabase PostgREST has a max-rows setting (typically 800-1000) that caps results per request.
// This helper fetches all pages to bypass that limit.
async function fetchAllPages(options: {
  columns: string
  dateFrom: string
  dateTo: string
  clientId?: string
  orderBy: string
  maxTotal?: number // Safety cap to prevent runaway queries
}): Promise<{ data: any[]; error: any }> {
  const PAGE_SIZE = 800 // Match Supabase PostgREST max-rows to avoid partial pages
  const maxTotal = options.maxTotal || 15000
  let allData: any[] = []
  let offset = 0
  let hasMore = true

  while (hasMore && offset < maxTotal) {
    let query = supabase
      .from('obsync_dynamic_reports')
      .select(options.columns)
      .eq('event_type', 'EMAIL_REPLY')
      .not('to_email', 'is', null)
      .not('from_email', 'is', null)
      .gte('created_at', options.dateFrom)
      .lt('created_at', options.dateTo)

    if (options.clientId) {
      query = query.eq('client_id', options.clientId)
    }

    query = query
      .order(options.orderBy, { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1)

    const result = await query
    if (result.error) {
      return { data: allData, error: result.error }
    }

    const pageData = result.data || []
    allData = allData.concat(pageData)

    console.log(`📄 Page ${Math.floor(offset / PAGE_SIZE) + 1}: fetched ${pageData.length} rows (total: ${allData.length})`)

    // If we got fewer rows than PAGE_SIZE, we've reached the end
    if (pageData.length < PAGE_SIZE) {
      hasMore = false
    } else {
      offset += PAGE_SIZE
    }
  }

  if (offset >= maxTotal) {
    console.warn(`⚠️ Reached safety cap of ${maxTotal} rows`)
  }

  return { data: allData, error: null }
}

// Optimized fetch function with automatic pagination to bypass PostgREST max-rows limit
async function fetchAutoRepliesOptimized(options?: { limit?: number; offset?: number; dateFrom?: string; dateTo?: string; clientId?: string }): Promise<AutoRepliesData> {
  // Calculate Month to Date date range (UK timezone) as default
  const now = new Date()
  const ukNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }))
  const defaultMonthStart = new Date(ukNow.getFullYear(), ukNow.getMonth(), 1)
    .toISOString().split('T')[0]

  const ukDate = new Date().toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).split('/').reverse().join('-')

  const defaultTomorrow = new Date(new Date(ukDate).getTime() + 24 * 60 * 60 * 1000)
    .toISOString().split('T')[0]

  // Use provided date range or default to Month to Date
  const monthStart = options?.dateFrom || defaultMonthStart
  const tomorrow = options?.dateTo || defaultTomorrow

  console.log(`🔄 Fetching auto replies: ${options?.dateFrom ? 'custom range' : 'Month to Date'} (${monthStart} to ${tomorrow})${options?.clientId ? ` client=${options.clientId}` : ''}`)

  const orderBy = 'event_timestamp'
  const startTime = performance.now()

  let data: any = null
  let error: any = null

  try {
    // Use pagination to fetch ALL matching rows (bypasses PostgREST max-rows cap)
    const result = await fetchAllPages({
      columns: REPLY_COLUMNS,
      dateFrom: monthStart,
      dateTo: tomorrow,
      clientId: options?.clientId,
      orderBy,
    })
    data = result.data
    error = result.error
  } catch (queryError: any) {
    // Catch JSON parsing errors from Supabase response
    if (queryError?.message?.includes('JSON') || queryError?.message?.includes('Unterminated')) {
      console.error('❌ JSON parsing error in Supabase response - likely corrupted data in database')
      error = queryError
    } else {
      error = queryError
    }
  }

  const endTime = performance.now()
  console.log(`⏱️  Query completed in ${Math.round(endTime - startTime)}ms, fetched ${data?.length || 0} rows`)

  let totalCount = data?.length || 0

  if (error) {
    console.error('❌ Error fetching auto replies:', error)
    throw error
  }

  console.log(`✅ Fetched ${data?.length || 0} auto replies from obsync_dynamic_reports`)

  // Fetch from_name from mailboxes table for all unique from_email addresses
  const uniqueFromEmails = [...new Set((data || []).map((r: any) => r.from_email).filter(Boolean))]
  let mailboxNameMap = new Map<string, string>()
  
  if (uniqueFromEmails.length > 0) {
    const { data: mailboxes } = await supabase
      .from('mailboxes')
      .select('email, from_name')
      .in('email', uniqueFromEmails)
    
    mailboxes?.forEach((mailbox: any) => {
      if (mailbox.email && mailbox.from_name) {
        mailboxNameMap.set(mailbox.email.toLowerCase(), mailbox.from_name)
      }
    })
  }

  // ROLLBACK: Transform obsync_dynamic_reports data to match AutoReply interface
  // Schema is now aligned - same field names in both tables
  const transformedReplies: AutoReply[] = (data || []).map((reply: any) => {
    // Safely handle reply_body - truncate if too long to prevent JSON issues
    let safeReplyBody = reply.reply_body
    if (safeReplyBody && typeof safeReplyBody === 'string' && safeReplyBody.length > 50000) {
      console.warn(`⚠️ Truncating very long reply_body for reply ${reply.id} (${safeReplyBody.length} chars)`)
      safeReplyBody = safeReplyBody.substring(0, 50000) + '...[truncated]'
    }
    
    return {
      id: reply.id,
      
      // Direct fields from obsync_dynamic_reports (schema aligned)
      to_email: reply.to_email,
      to_name: reply.to_name,
      from_email: reply.from_email,
      from_name: mailboxNameMap.get(reply.from_email?.toLowerCase() || '') || null,
      campaign_name: reply.campaign_name,
      campaign_id: reply.campaign_id,
      subject: reply.subject,
      reply_body: safeReplyBody,
      
      // AI categorization fields (direct from database columns)
      response_category: reply.response_category,
      response_category_id: reply.response_category_id,
      reply_category_type: reply.reply_category_type,
      reasoning: reply.reasoning,
      ai_model_used: reply.ai_model_used,
      categorized_at: reply.categorized_at,
      
      // Smartlead sync tracking fields
      smartlead_sync_status: reply.smartlead_sync_status,
      smartlead_sync_attempted_at: reply.smartlead_sync_attempted_at,
      smartlead_sync_error: reply.smartlead_sync_error,
      
      // Smartlead ignore reply tracking fields
      smartlead_ignore_status: reply.smartlead_ignore_status,
      smartlead_ignore_attempted_at: reply.smartlead_ignore_attempted_at,
      smartlead_ignore_error: reply.smartlead_ignore_error,
      smartlead_ignore_retry_count: reply.smartlead_ignore_retry_count,
      
      // SDR Response Tracking fields
      average_time_to_respond: reply.average_time_to_respond,
      lead_without_a_reply: reply.lead_without_a_reply,
      sdr_response_tracking_status: reply.sdr_response_tracking_status,
      first_sdr_response_timestamp: reply.first_sdr_response_timestamp,
      first_positive_reply_timestamp: reply.first_positive_reply_timestamp,
      last_sdr_poll_at: reply.last_sdr_poll_at,
      
      // Slack notification tracking
      slack_notification_sent: reply.slack_notification_sent,
      
      // Event data
      event_type: reply.event_type,
      event_timestamp: reply.event_timestamp || reply.created_at,
      created_at: reply.created_at,
      updated_at: reply.updated_at,
      client_id: reply.client_id,
      
      // SmartLead fields
      sl_email_lead_id: reply.sl_email_lead_id,
      sl_lead_email: reply.sl_lead_email || reply.to_email,
      app_url: reply.app_url,
      secret_key: null,
      reply_count: reply.reply_count,
      stats_id: reply.stats_id,
      message_id: reply.message_id,
      webhook_id: reply.webhook_id,
      
      // Additional fields
      time_replied: reply.event_timestamp || reply.created_at,
      preview_text: reply.preview_text || null, // reply_body not loaded to prevent JSON errors
      description: reply.description || null,
      // sent_message and reply_body excluded from query to prevent JSON parsing errors
      // These can be fetched on-demand if needed via a separate query
      reply_body: null, // Not loaded in initial query to prevent JSON parsing errors
      sent_message: null, // Not loaded in initial query to prevent JSON parsing errors
      sent_message_body: null, // Not loaded for performance (can be fetched on demand if needed)
      
      // Thread data
      thread_id: null,
      reply_sequence: null,
      
      // Extract company domain from email for enrichment
      company_domain: reply.to_email?.split('@')[1],
      
      // Region from database column
      region: reply.region,
      
      // Store minimal data reference (not full JSONB for performance)
      data: {}, // Empty object - full data not loaded for performance
      
      // AI confidence
      ai_confidence: reply.confidence_score
    }
  })

  return {
    count: totalCount || 0,
    replies: transformedReplies
  }
}

export function useAutoReplies(options?: { initialLimit?: number; dateRange?: { from: Date | undefined; to: Date | undefined }; clientId?: string }) {
  const initialLimit = options?.initialLimit || 10000 // High limit to avoid truncating results when custom date range is selected
  const dateRange = options?.dateRange
  const clientId = options?.clientId
  
  // Load cached data synchronously BEFORE React Query initializes
  // This ensures cache is available immediately on hard refresh
  const cachedDataRef = React.useRef<AutoRepliesData | null>(null)
  
  // Load cache synchronously on first render (before React Query)
  if (cachedDataRef.current === null) {
    try {
      const cached = AutoRepliesCache.load()
      // Use cache if it exists and has data (don't restrict by limit for initial display)
      if (cached && cached.replies && cached.replies.length > 0) {
        cachedDataRef.current = cached
        console.log(`✅ Loaded ${cached.replies.length} replies from cache`)
      }
    } catch (cacheError) {
      // If cache loading fails (corrupted JSON), clear it and continue without cache
      console.error('❌ Failed to load cache, clearing corrupted cache:', cacheError)
      AutoRepliesCache.clear()
      cachedDataRef.current = null
    }
  }

  // Compute date range strings for the query (stable values for query key)
  const dateFromStr = dateRange?.from ? dateRange.from.toISOString().split('T')[0] : undefined
  const dateToStr = dateRange?.to
    ? new Date(dateRange.to.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Add 1 day to include the end date fully
    : undefined

  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['auto-replies-optimized', initialLimit, dateFromStr, dateToStr, clientId],
    queryFn: async () => {
      try {
        const freshData = await fetchAutoRepliesOptimized({ limit: initialLimit, offset: 0, dateFrom: dateFromStr, dateTo: dateToStr, clientId })
        // Only save to cache for default MTD query (don't overwrite MTD cache with custom range/client data)
        if (!dateFromStr && !dateToStr && !clientId) {
          try {
            AutoRepliesCache.save(freshData)
          } catch (cacheError) {
            console.warn('⚠️ Failed to save to cache (non-critical):', cacheError)
          }
        }
        // Update ref
        cachedDataRef.current = freshData
        return freshData
      } catch (queryError: any) {
        // If query fails with JSON error, it might be a cache issue - clear it
        if (queryError?.message?.includes('JSON') || queryError?.message?.includes('Unterminated')) {
          console.error('❌ JSON parsing error detected, clearing cache...')
          AutoRepliesCache.clear()
        }
        throw queryError
      }
    },
    // Only use cached data for default MTD query (no custom date range or client filter)
    initialData: (!dateFromStr && !dateToStr && !clientId) ? (cachedDataRef.current || undefined) : undefined,
    initialDataUpdatedAt: (!dateFromStr && !dateToStr && !clientId && cachedDataRef.current) ? (AutoRepliesCache.getCachedAt() || undefined) : undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep cached data longer
    refetchInterval: 60000, // 60 seconds - less aggressive refetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    // Always refetch in background, but show cached data immediately
    // React Query will use initialData first, then refetch in background
    refetchOnMount: true, // Refetch in background even if we have cached data
    refetchOnReconnect: true,
    retry: 2, // Reduce retries
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000), // Faster retry
    // Use placeholderData to show cached data immediately while refetching (only for default MTD)
    placeholderData: (previousData) => {
      if (dateFromStr || dateToStr || clientId) return previousData // Don't use MTD cache for custom ranges/filters
      return cachedDataRef.current || previousData
    },
    // Optimistic updates
    refetchIntervalInBackground: false, // Don't refetch in background
  })

  // ROLLBACK: Real-time subscription to obsync_dynamic_reports table
  // Set up real-time subscription for obsync_dynamic_reports table
  React.useEffect(() => {
    // Only set up subscription if we have data loaded
    if (!data) return
    
    console.log('🔔 Setting up real-time subscription for auto replies (obsync_dynamic_reports)')
    
    const channel = supabase
      .channel('obsync_replies_changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all changes (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'obsync_dynamic_reports',
          filter: 'event_type=eq.EMAIL_REPLY'
        },
        (payload) => {
          console.log('🔔 Real-time update received:', payload)
          // Refetch data when EMAIL_REPLY events change (but only if data is stale)
          const timeSinceUpdate = Date.now() - dataUpdatedAt
          if (timeSinceUpdate > 5 * 60 * 1000) { // Only refetch if data is older than 5 minutes
            refetch()
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Subscription status:', status)
      })

    return () => {
      console.log('🔌 Cleaning up real-time subscription')
      supabase.removeChannel(channel)
    }
  }, [data, refetch, dataUpdatedAt])

  // Ensure we always return data - prefer cached data if query hasn't loaded yet
  const displayData = data || cachedDataRef.current
  
  return {
    data: displayData,
    count: displayData?.count || 0,
    replies: displayData?.replies || [],
    isLoading: isLoading && !cachedDataRef.current, // Only show loading if no cache
    isFetching,
    error,
    refetch,
  }
}